import { Controller } from '@hotwired/stimulus';
import { generateRecoveryCode } from '../vault_crypto';
import { saveText } from '../save_blob';

export default class extends Controller {
  static targets = [
    'passwordField',
    'confirmationField',
    'nextButton',
    'passwordError',
    'confirmationError',
    'keyField',
    'recoveryCodeDisplay',
    'codeSection',
    'copyLabel',
    'generateButton',
    'downloadButton',
    'downloadedBadge',
    'completeButton',
  ];

  static values = { minLength: Number, t: Object };

  // ----- lifecycle ----------------------------------------------------------

  initialize() {
    this._keypairGenerationInProgress = false; // blocks overlapping async runs
    this._keypairGenerated = false; // allow only one successful run while on step 1
    this._recoveryCodeGenerated = false; // true once generateRecoveryCode has run
    this._downloaded = false; // true once recovery code has been downloaded
    this._stepObserver = null; // watches visibility of step 1 to reset flags
    this.keyFieldObserver = null; // watches key field type change to "text"
  }

  connect() {
    // set up key generator
    window.keygen ||= new KeyGen(this._minLen());

    // observe key field becoming visible (type="text") to enable confirm button
    if (this.hasKeyFieldTarget) {
      this.keyFieldObserver = new MutationObserver(() => {
        const inputType = this.keyFieldTarget.getAttribute('type');
        if (inputType === 'text') {
          this.keyFieldObserver?.disconnect();
          this.keyFieldObserver = null;
        }
      });
      this.keyFieldObserver.observe(this.keyFieldTarget, {
        attributes: true,
        attributeFilter: ['type'],
      });
    }

    // live validation
    if (this.hasPasswordFieldTarget) {
      this.passwordFieldTarget.addEventListener('input', () => this.validateFields());
    }
    if (this.hasConfirmationFieldTarget) {
      this.confirmationFieldTarget.addEventListener('input', () => this.validateFields());
    }

    // reset guards whenever step 1 becomes visible again
    this.observePasswordStepVisibility();

    // Also listen for multistep navigation events to reset state
    this.element.addEventListener('multistep:step-changed', (event) => {
      const currentStep = event.detail?.currentStep;
      if (
        currentStep &&
        this.hasPasswordFieldTarget &&
        currentStep.contains(this.passwordFieldTarget)
      ) {
        this._resetGenerationState();
      }
    });
  }

  disconnect() {
    this.keyFieldObserver?.disconnect();
    this.keyFieldObserver = null;
    this._stepObserver?.disconnect();
    this._stepObserver = null;
  }

  // ----- actions / handlers -------------------------------------------------

  // Bind via data-action (e.g., keydown.enter->encryption-flow#handleKeyDown)
  handleKeyDown(event) {
    const multistep = Stimulus.getControllerForElementAndIdentifier(this.element, 'multistep');
    if (!multistep) return;

    const currentStep = multistep.stepTargets[multistep.currentIndex];
    const inPasswordStep =
      this.hasPasswordFieldTarget &&
      this.hasConfirmationFieldTarget &&
      currentStep.contains(this.passwordFieldTarget) &&
      currentStep.contains(this.confirmationFieldTarget);

    event.preventDefault();

    if (inPasswordStep) {
      this.handleKeyStep(event);
    }
  }

  async handleKeyStep(event) {
    // block key auto-repeat and duplicate/in-flight calls
    if (event?.repeat) return;
    event?.preventDefault();
    if (this._keypairGenerationInProgress || this._keypairGenerated) return;

    this._keypairGenerationInProgress = true;
    try {
      window.keygen ||= new KeyGen(this._minLen());

      const password = this.hasPasswordFieldTarget ? this.passwordFieldTarget.value : '';
      const confirmation = this.hasConfirmationFieldTarget
        ? this.confirmationFieldTarget.value
        : '';
      const minLength = this._minLen();

      if (!this.isValid(password, confirmation, minLength)) {
        this.validateFields();
        return;
      }

      const isLeaked = this._recoveryCodeGenerated ? false : await this.checkHIBP(password);
      if (isLeaked) {
        alert(this.tValue.password_breached);
        if (this.hasPasswordFieldTarget) this.passwordFieldTarget.value = '';
        if (this.hasConfirmationFieldTarget) this.confirmationFieldTarget.value = '';
        this.validateFields();
        return;
      }

      if (password) {
        if (this.hasKeyFieldTarget) this.keyFieldTarget.value = password;
      }

      await window.keygen.generateKeyPair();
      window.keygen.fillForm();

      this.dispatch('ready');
      this.next();

      // success: allow only once while on step 1; resets if user navigates back
      this._keypairGenerated = true;
    } catch (e) {
      console.error('encryption-flow#handleKeyStep error', e);
    } finally {
      this._keypairGenerationInProgress = false;
    }
  }

  generateRecoveryCode(event) {
    event?.preventDefault();
    const code = generateRecoveryCode();

    if (this.hasPasswordFieldTarget) this.passwordFieldTarget.value = code;
    if (this.hasConfirmationFieldTarget) this.confirmationFieldTarget.value = code;

    if (this.hasRecoveryCodeDisplayTarget) this.recoveryCodeDisplayTarget.value = code;

    // reveal the code only after it has been generated (button comes first)
    if (this.hasCodeSectionTarget) this.codeSectionTarget.classList.remove('hidden');

    // lock generation: a second click would silently overwrite the code the user
    // was just told "won't be shown again". Re-enabled only on a step reset.
    if (this.hasGenerateButtonTarget) this.generateButtonTarget.disabled = true;

    this._recoveryCodeGenerated = true;
    if (this.hasNextButtonTarget) this.nextButtonTarget.disabled = false;
  }

  async copyRecoveryCode(event) {
    event?.preventDefault();
    const code = this.hasRecoveryCodeDisplayTarget ? this.recoveryCodeDisplayTarget.value : '';
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code);
    } catch {
      return; // clipboard blocked (e.g. insecure context); leave the field for manual copy
    }

    if (this.hasCopyLabelTarget) {
      this.copyLabelTarget.textContent = this.tValue.copied;
      clearTimeout(this._copyResetTimer);
      this._copyResetTimer = setTimeout(() => {
        if (this.hasCopyLabelTarget) this.copyLabelTarget.textContent = this.tValue.copy;
      }, 1500);
    }
  }

  downloadKey(event) {
    event?.preventDefault();

    const password =
      document.querySelector('#crypt-visible-key')?.value ||
      (this.hasKeyFieldTarget ? this.keyFieldTarget.value : '');

    if (!password) {
      alert(this.tValue.key_not_found);
      return;
    }

    saveText(password, 'recovery-code.txt');

    this._downloaded = true;

    // show progress: swap the download button for a "Downloaded ✓" badge and
    // activate Complete setup so the next step is unmistakable.
    if (this.hasDownloadButtonTarget) this.downloadButtonTarget.classList.add('hidden');
    if (this.hasDownloadedBadgeTarget) this.downloadedBadgeTarget.classList.remove('hidden');
    if (this.hasCompleteButtonTarget) this.completeButtonTarget.disabled = false;
  }

  completeSetup(event) {
    event?.preventDefault();
    if (!this._downloaded) return;
    this.element.requestSubmit();
  }

  next() {
    const multistep = Stimulus.getControllerForElementAndIdentifier(this.element, 'multistep');
    multistep?.next?.(new Event('next'));
    requestAnimationFrame(() => {
      this.element.scrollIntoView({ block: 'start' });
    });
  }

  goBack() {
    const multistep = Stimulus.getControllerForElementAndIdentifier(this.element, 'multistep');
    if (multistep) {
      // Use the original multistep back navigation
      multistep.back?.(new Event('back'));

      // Also reset generation state immediately since we're going back
      this._resetGenerationState();
    }
  }

  // ----- validation ---------------------------------------------------------

  validateFields() {
    if (!(this.hasPasswordFieldTarget && this.hasConfirmationFieldTarget)) return;

    const password = this.passwordFieldTarget.value.trim();
    const confirmation = this.confirmationFieldTarget.value.trim();
    const min = this._minLen();
    let valid = true;

    if (!password || password.length < min) {
      if (this.hasPasswordErrorTarget) {
        this.passwordErrorTarget.textContent = this.tValue.password_too_short;
        this.passwordErrorTarget.classList.remove('hidden');
      }
      valid = false;
    } else if (this.hasPasswordErrorTarget) {
      this.passwordErrorTarget.textContent = '';
      this.passwordErrorTarget.classList.add('hidden');
    }

    if (!confirmation) {
      if (this.hasConfirmationErrorTarget) {
        this.confirmationErrorTarget.textContent = '';
        this.confirmationErrorTarget.classList.add('hidden');
      }
      valid = false;
    } else if (password !== confirmation) {
      if (this.hasConfirmationErrorTarget) {
        this.confirmationErrorTarget.textContent = this.tValue.passwords_do_not_match;
        this.confirmationErrorTarget.classList.remove('hidden');
      }
      valid = false;
    } else if (this.hasConfirmationErrorTarget) {
      this.confirmationErrorTarget.textContent = '';
      this.confirmationErrorTarget.classList.add('hidden');
    }

    if (this.hasNextButtonTarget) this.nextButtonTarget.disabled = !valid;
  }

  isValid(password, confirmation, minLength) {
    return password.length >= minLength && password === confirmation;
  }

  async checkHIBP(password) {
    // skip external call in test env; avoid ReferenceError if railsEnv is unset
    if (typeof window.railsEnv === 'string' && window.railsEnv === 'test') return false;

    const sha1 = async (str) => {
      const buf = await crypto.subtle.digest('SHA-1', new TextEncoder().encode(str));
      return [...new Uint8Array(buf)]
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('')
        .toUpperCase();
    };

    const hash = await sha1(password);
    const prefix = hash.substring(0, 5);
    const suffix = hash.substring(5);
    const res = await fetch(`https://api.pwnedpasswords.com/range/${prefix}`);
    const text = await res.text();
    return text.includes(suffix);
  }

  // ----- misc helpers -------------------------------------------------------

  observePasswordStepVisibility() {
    const multistep = Stimulus.getControllerForElementAndIdentifier(this.element, 'multistep');
    if (!multistep) return;

    const stepEl = multistep.stepTargets.find((step) =>
      this.hasPasswordFieldTarget ? step.contains(this.passwordFieldTarget) : false
    );
    if (!stepEl) return;

    const onChange = () => {
      if (this._isVisible(stepEl)) this._resetGenerationState();
    };

    this._stepObserver?.disconnect();
    this._stepObserver = new MutationObserver(onChange);
    this._stepObserver.observe(stepEl, {
      attributes: true,
      attributeFilter: ['hidden', 'class', 'style'],
    });
    onChange(); // initialize
  }

  _isVisible(el) {
    return el && !el.hidden && !el.classList.contains('hidden') && el.offsetParent !== null;
  }

  _resetGenerationState() {
    this._keypairGenerationInProgress = false;
    this._keypairGenerated = false;
    this._recoveryCodeGenerated = false;
    this._downloaded = false;
    if (this.hasPasswordFieldTarget) this.passwordFieldTarget.value = '';
    if (this.hasConfirmationFieldTarget) this.confirmationFieldTarget.value = '';
    if (this.hasRecoveryCodeDisplayTarget) this.recoveryCodeDisplayTarget.value = '';
    if (this.hasCodeSectionTarget) this.codeSectionTarget.classList.add('hidden');
    clearTimeout(this._copyResetTimer);
    if (this.hasCopyLabelTarget) this.copyLabelTarget.textContent = this.tValue.copy;
    if (this.hasGenerateButtonTarget) this.generateButtonTarget.disabled = false;
    if (this.hasDownloadButtonTarget) this.downloadButtonTarget.classList.remove('hidden');
    if (this.hasDownloadedBadgeTarget) this.downloadedBadgeTarget.classList.add('hidden');
    if (this.hasNextButtonTarget) this.nextButtonTarget.disabled = true;
    if (this.hasCompleteButtonTarget) this.completeButtonTarget.disabled = true;
    // Re-validate fields to update button state when returning to step 1
    this.validateFields();
  }

  _minLen() {
    return this.hasMinLengthValue ? this.minLengthValue : 12;
  }
}
